import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { withError } from '@/react-tools/react-query';
import axios, { parseAxiosError } from '@/portainer/services/axios';

import { AlertRulesResponse, AlertRuleRow } from './types';

const queryKeys = {
  base: () => ['alerts', 'active'],
};

export function useAlertRules<T = AlertRulesResponse>({
  select,
  refetchInterval,
}: {
  select?: (data: AlertRulesResponse) => T;
  refetchInterval?: number;
}) {
  return useQuery(queryKeys.base(), async () => getAlertRules(), {
    ...withError('Unable to get alerts'),
    select,
    refetchInterval,
  });
}

export async function getAlertRules() {
  try {
    const { data } = await axios.get<AlertRulesResponse>(
      `/observability/alerting/rules`
    );
    return data;
  } catch (error) {
    throw parseAxiosError(error);
  }
}

export async function updateAlertRule(alertRule: AlertRuleRow) {
  try {
    const { data } = await axios.put<AlertRuleRow>(
      `/observability/alerting/rules/${alertRule.id}`,
      { AlertingRule: alertRule }
    );
    return data;
  } catch (error) {
    throw parseAxiosError(error);
  }
}

export function useUpdateAlertRule() {
  const queryClient = useQueryClient();

  return useMutation(updateAlertRule, {
    ...withError('Unable to update alert rule'),
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.base());
    },
  });
}
